import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { startOfDay, differenceInCalendarDays, addDays, format } from 'date-fns';
import { Box, useTheme } from '@mui/material';
import { assignmentToGanttBar } from "./helpers";
import ConfirmDialog from 'components/ConfirmDialog';
import Timeline from './Timeline';
import NowLine from './NowLine';
import DroppableCell from './DroppableCell';
import GanttBar from "./GanttBar";

const VIRT_BUFFER = 10;

/* ── EmployeeRow - memoized per row ────────────────────────────────────────────
   Re-renders only when:
   - row data changes (new/removed assignments)
   - layout props change (rowHeight, cellWidth, gridWidth)
   - selectedBarId changes AND a bar in THIS row is affected
──────────────────────────────────────────────────────────────────────────────── */
const EmployeeRow = React.memo(({
    row,
    rowHeight,
    gridWidth,
    cellWidth,
    startDate,
    selectedBarId,
    onBarClick,
    onBarDoubleClick,
}) => {
    const bars = useMemo(() =>
        (row.assignments || []).map(a => {
            const bar = assignmentToGanttBar(a, startDate);
            if (!bar) return null;
            const uniqueId = `${bar.id}-${row.id}`;
            return { bar, uniqueId };
        }).filter(Boolean),
        [row.assignments, row.id, startDate]
    );

    return (
        <Box sx={{ height: rowHeight, position: 'relative' }}>
            <DroppableCell
                employeeId={row.id}
                width={gridWidth}
                rowHeight={rowHeight}
            />
            {bars.map(({ bar, uniqueId }) => (
                <GanttBar
                    key={uniqueId}
                    bar={{
                        ...bar,
                        uniqueId,
                        dragSource: 'EMPLOYEE_ROW',
                        fromEmployeeId: row.id,
                    }}
                    cellWidth={cellWidth}
                    rowHeight={rowHeight}
                    isActive={selectedBarId === uniqueId}
                    isOptimistic={!!bar.__optimistic}
                    onClick={onBarClick}
                    onDoubleClick={onBarDoubleClick}
                />
            ))}
        </Box>
    );
}, (prev, next) => {
    if (prev.row          !== next.row)          return false;
    if (prev.rowHeight    !== next.rowHeight)    return false;
    if (prev.gridWidth    !== next.gridWidth)    return false;
    if (prev.cellWidth    !== next.cellWidth)    return false;
    if (prev.startDate    !== next.startDate)    return false;
    if (prev.onBarClick   !== next.onBarClick)   return false;
    if (prev.onBarDoubleClick !== next.onBarDoubleClick) return false;

    if (prev.selectedBarId === next.selectedBarId) return true;

    // selectedBarId changed - only re-render if a bar in this row is affected.
    // uniqueId pattern: `${assignmentId}-${rowId}` (both 24-char hex, no dashes)
    const rowId = next.row.id;
    const touches = (uid) => uid != null && uid.endsWith(`-${rowId}`);
    return !touches(prev.selectedBarId) && !touches(next.selectedBarId);
});


const TopRightPanel = ({
    syncX,
    syncY,
    timelineScrollRef,
    topLeftScrollRef,
    topRightScrollRef,
    bottomRightScrollRef,
    rowHeight = 40,
    cellWidth = 8,
    startDate,
    endDate,
    employees = [],
    selectedBarId,
    onBarClick,
    onBarDoubleClick,
    canDelete = false,
    onUnassign,
}) => {
    const theme = useTheme();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [vRange, setVRange] = useState({ start: 0, end: 200 });

    const baseDate = startDate && startOfDay(new Date(startDate));

    /* ---------- O(1) SELECTED BAR LOOKUP ----------
       uniqueId = `${assignmentId}-${fromEmployeeId}` - both 24-char hex MongoDB ObjectIds
       So slice at index 24 to split without scanning.
    ------------------------------------------------- */
    const employeeMap = useMemo(
        () => new Map(employees.map(e => [e.id, e])),
        [employees]
    );

    const selectedBarInfo = useMemo(() => {
        if (!selectedBarId || selectedBarId.startsWith('unassigned-')) return null;
        const assignmentId = selectedBarId.slice(0, 24);
        const fromEmployeeId = selectedBarId.slice(25);
        const row = employeeMap.get(fromEmployeeId);
        if (!row) return null;
        const rawAssignment = (row.assignments || []).find(a => String(a._id) === assignmentId);
        if (!rawAssignment) return null;
        const bar = assignmentToGanttBar(rawAssignment, startDate);
        return bar ? { assignmentId, fromEmployeeId, rawAssignment, bar } : null;
    }, [selectedBarId, employeeMap, startDate]);

    /* ---------- UNASSIGN ON DELETE KEY ---------- */
    useEffect(() => {
        if (!selectedBarInfo || !canDelete) return;
        const onKeyDown = (e) => {
            if (e.key !== 'Delete') return;
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
            setConfirmOpen(true);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [selectedBarInfo, canDelete]);

    const datesCount = useMemo(() => {
        if (!startDate || !endDate) return 0;
        return differenceInCalendarDays(addDays(endDate, 2), startDate);
    }, [startDate, endDate]);

    const gridDimensions = useMemo(() => ({
        width:  datesCount * 24 * cellWidth,
        height: (employees?.length || 0) * rowHeight,
    }), [datesCount, cellWidth, employees?.length, rowHeight]);

    const gridLines = useMemo(() => {
        const d = theme.palette.divider;
        const dayW = cellWidth * 24;
        return {
            backgroundImage: [
                `linear-gradient(to right, ${d} 1px, transparent 1px)`,
                `linear-gradient(to bottom, ${d} 1px, transparent 1px)`,
            ].join(', '),
            backgroundSize: `${dayW}px 100%, 100% ${rowHeight}px`,
        };
    }, [cellWidth, rowHeight, theme.palette.divider]);

    const hasAutoScrolled = useRef(false);

    /* -------------------- SCROLL SYNC + VIRTUAL LIST -------------------- */
    const handleScroll = useCallback((e) => {
        syncX(e.target, [
            timelineScrollRef.current,
            bottomRightScrollRef.current,
        ]);
        syncY(e.target, [
            topLeftScrollRef.current,
        ]);

        const st = e.target.scrollTop;
        const ch = e.target.clientHeight;
        const newStart = Math.max(0, Math.floor(st / rowHeight) - VIRT_BUFFER);
        const newEnd = Math.min(employees.length, Math.ceil((st + ch) / rowHeight) + VIRT_BUFFER);
        setVRange(prev =>
            prev.start !== newStart || prev.end !== newEnd
                ? { start: newStart, end: newEnd }
                : prev
        );
    }, [syncX, syncY, timelineScrollRef, topLeftScrollRef, bottomRightScrollRef, rowHeight, employees.length]);

    /* -------------------- AUTO SCROLL TO NOW -------------------- */
    useEffect(() => {
        if (hasAutoScrolled.current) return;
        if (!topRightScrollRef?.current || !baseDate || !cellWidth) return;

        const now = new Date();
        const diffHours = (now - baseDate) / (1000 * 60 * 60);
        const maxHours = datesCount * 24;

        if (diffHours < 0 || diffHours > maxHours) return;

        hasAutoScrolled.current = true;
        const container = topRightScrollRef.current;
        const targetScrollLeft = diffHours * cellWidth - container.clientWidth / 2;
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        container.scrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));
    }, [baseDate, cellWidth, datesCount, topRightScrollRef]);

    /* -------------------- VIRTUAL SLICE -------------------- */
    const visibleEmployees = employees.slice(vRange.start, vRange.end);
    const virtualPaddingTop = vRange.start * rowHeight;

    return (
        <>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Timeline */}
            <Box sx={{ height: rowHeight, flexShrink: 0, overflow: 'hidden' }}>
                <Timeline
                    syncX={syncX}
                    ref={timelineScrollRef}
                    topRightScrollRef={topRightScrollRef}
                    bottomRightScrollRef={bottomRightScrollRef}
                    cellWidth={cellWidth}
                    rowHeight={rowHeight}
                    from={startDate}
                    to={endDate ? addDays(endDate, 1) : null}
                />
            </Box>

            {/* Grid */}
            <Box
                ref={topRightScrollRef}
                onScroll={handleScroll}
                sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    position: 'relative',
                    bgcolor: 'background.default',
                }}
            >
                {/* Full-height container maintains correct scrollbar size */}
                <Box
                    sx={{
                        position: 'relative',
                        width: gridDimensions.width,
                        height: gridDimensions.height,
                        minHeight: '100%',
                        ...gridLines,
                    }}
                >
                    <NowLine
                        baseDate={baseDate}
                        datesCount={datesCount}
                        cellWidth={cellWidth}
                    />
                    {/* Only visible rows rendered, offset to correct scroll position */}
                    <Box sx={{ position: 'absolute', top: virtualPaddingTop, left: 0, width: '100%' }}>
                        {visibleEmployees.map((row) => (
                            <EmployeeRow
                                key={row.id}
                                row={row}
                                rowHeight={rowHeight}
                                gridWidth={gridDimensions.width}
                                cellWidth={cellWidth}
                                startDate={startDate}
                                selectedBarId={selectedBarId}
                                onBarClick={onBarClick}
                                onBarDoubleClick={onBarDoubleClick}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>

        <ConfirmDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={() => {
                if (!selectedBarInfo) return;
                onUnassign?.(selectedBarInfo.assignmentId, selectedBarInfo.fromEmployeeId, selectedBarInfo.rawAssignment);
            }}
            title="Confirm?"
            message={selectedBarInfo ? `Unassign shift "${selectedBarInfo.bar.shiftCode}" on ${format(new Date(selectedBarInfo.bar.date), 'dd/MM/yyyy')}?` : ''}
            confirmText="Proceed"
            confirmColor="error"
        />
        </>
    );
};

export default React.memo(TopRightPanel);
